# beSharp Sessions Module

## Table of Contents

- [Overview](#overview)
- [Components](#components)
- [Services](#services)
- [Configuration](#configuration)
- [Storage Providers](#storage-providers)
- [Multiple Sessions](#multiple-sessions)
- [Multiple Authentication Providers](#multiple-authentication-providers)
- [Usage Examples](#usage-examples)

## Overview

The `beSharp Sessions Module` provides authentication and session management functionalities for Angular applications. It allows users to sign in using different authentication providers, manage multiple active sessions, and securely store session information. The module is designed to be flexible and configurable, supporting various authentication flows and storage options.

## Components

### [`OAuth2CallbackComponent`](src/app/modules/besharp-sessions/lib/components/oauth2-callback/oauth2-callback.component.ts) (aliased as `OAuth2CallbackReceiver`)

Handles the OAuth2 callback after a successful authentication. It extracts the authentication token and other relevant information from the URL and stores it in the session.

### [`SessionSigninComponent`](src/app/modules/besharp-sessions/lib/components/session-signin/session-signin.component.ts) (aliased as `SignInDialog`)

Presents a sign-in form to the user, allowing them to authenticate using a configured authentication provider. It supports different types of credentials and provides pre-filling capabilities.

### [`SessionSelectorComponent`](src/app/modules/besharp-sessions/lib/components/session-selector/session-selector.component.ts) (aliased as `SessionSelector`)

Displays a list of available sessions and allows the user to select one to activate. If no sessions are available, it navigates the user to the sign-in page.

### [`DropdownSessionSelectorComponent`](src/app/modules/besharp-sessions/lib/components/active-session-badge/active-session-badge.component.ts) (aliased as `DropdownSessionSelector`)

Displays the currently active session as a badge in a dropdown menu. It allows the user to switch between sessions or sign out.

## Services

### [`SessionsService`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts)

The core service for managing authentication sessions. It provides methods for:

- Starting a new session using an [`AuthProvider`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts).
- Refreshing an existing session.
- Activating a session.
- Getting the active session.
- Getting all stored sessions.
- Removing a session.
- Setting session claims.
- Getting an authentication provider by name.

It also handles session persistence using a [`StorageProvider`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts).

## Configuration

To configure the beSharp Sessions Module from scratch, you need to provide a configuration object that implements the `SessionsServiceConfig` interface. 

This is typically done by editing the pre-existing `DefaultSessionsServiceConfigProvider` class, which implements the `SessionsServiceConfigProvider` interface.

The configuration includes:

- **Session Management Options**:  
    - `autoRefreshCredentials`: Whether to automatically refresh credentials before they expire [Currently not supported].
    - `persistSessions`: Whether to persist sessions across browser reloads.
    - `sessionDiscriminator`: A function to uniquely identify sessions (usually by `session.id`), regardless of what authentication method was selected (The same user logging in via two different auth methods should yield the same session discriminator, using the user ID is a good choice).  

- **Navigation Callbacks**:  
    - `onSelectionRequired`: Called when a session selection is required (e.g., navigates to a session selection page).
    - `onSignInRequired`: Called when sign-in is required (e.g., navigates to a sign-in page, optionally pre-filling session info).
    - `onSessionActivated`: Called when a session is activated (e.g., navigates to the home page).

- **Authentication Providers**:  
    The `authProviders` array defines the available authentication methods. Each provider is instantiated with its own configuration, such as:
    - `OAuth2AuthProvider` for OAuth2 flows (e.g., username/password, authorization code).
        OAuth2 providers expose the following configuration parameters:
        - `clientId`, `clientSecret`, etc.
        - `grantType`: The OAuth2 grant type: 
            - `UsernamePasswordOAuth2GrantType` – Resource Owner Password Credentials Grant (username/password).
            - `AuthorizationCodeOAuth2GrantType` – Authorization Code Grant.
            - `ClientCredentialsOAuth2GrantType` – Client Credentials Grant.
    - `CustomTokenProvider` for custom authentication logic.

    Each provider configuration can specify:
    - `label`, `icon`, 
    - `redirectUrl`, `scopes`, `authorizationUrl`, `tokenUrl`.
    - `extractClaimsFromToken`: Function to extract user claims from the token.
    - `extractSessionIdFromToken`: Function to extract a unique session ID from the token.
    - `fields`: For custom providers, defines the fields required for authentication.

**Example:**

```typescript
@Injectable()
export class DefaultSessionsServiceConfigProvider implements SessionsServiceConfigProvider {
    constructor(private router: Router) {}

    public getConfig(): SessionsServiceConfig {
        return {
            autoRefreshCredentials: true,
            persistSessions: true,
            onSelectionRequired: () =>
                this.router.navigate(['session', 'select']),
            onSignInRequired: (session) =>
                this.router.navigate(['session', 'signin'], { queryParams: { prefill_session: session.id } }),
            onSessionActivated: () => this.router.navigate(['']),
            sessionDiscriminator: (session) => session.id,
            authProviders: [
                new OAuth2AuthProvider('oauth2_username_password', {
                    label: 'OAuth2 - Username + Password',
                    icon: 'pi pi-key',
                    clientId: 'your-client-id',
                    clientSecret: 'your-client-secret',
                    grantType: new UsernamePasswordOAuth2GrantType(),
                    redirectUrl: 'http://localhost:4200/session/oauth2/callback',
                    scopes: ['basic'],
                    tokenUrl: 'http://localhost:8080/realms/master/protocol/openid-connect/token',
                    extractClaimsFromToken: async (access_token) => ({
                        id: access_token['sub'] ?? '',
                        username: access_token['azp'] ?? '',
                    }),
                    extractSessionIdFromToken: async (access_token) => access_token.sub!,
                }),
                new OAuth2AuthProvider('oauth2_authorization_code', {
                    label: 'OAuth2 - Authorization Code',
                    icon: 'pi pi-google',
                    clientId: 'your-client-id',
                    grantType: new AuthorizationCodeOAuth2GrantType(),
                    redirectUrl: 'http://localhost:4200/session/oauth2/callback',
                    scopes: ['email', 'openid', 'phone'],
                    authorizationUrl: 'https://your-auth-domain/oauth2/authorize',
                    tokenUrl: 'https://your-auth-domain/oauth2/token',
                    extractClaimsFromToken: async (_, id_token) => ({
                        id: id_token.sub,
                        username: id_token['email'],
                    }),
                    extractSessionIdFromToken: async (_, id_token) => id_token.sub!,
                }),
                new CustomTokenProvider('custom_token', {
                    label: 'Custom Token',
                    icon: 'pi pi-cog',
                    authenticate: async (credentials) => ({
                        accessToken: 'token',
                        refreshToken: 'refreshToken',
                        expiresAt: Date.now() + 30 * 1000,
                        claims: {
                            id: credentials['email'],
                            username: credentials['email'],
                            imageUri: 'https://example.com/avatar.png',
                        },
                    }),
                    fields: [
                        { key: 'email', type: 'email', icon: 'pi pi-at', label: 'Email', saveForPrefill: true },
                    ],
                }),
            ],
        };
    }
}
```

**Summary:**  
To configure the module, edit the [`DefaultSessionsServiceConfigProvider`](src/app/modules/besharp-sessions/config/sessions.config.ts) object (or implement your own [`SessionsServiceConfigProvider`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts)), specifying session options, navigation callbacks, and an array of authentication providers with their respective settings and token extraction logic.

## Storage Providers

In the context of the beSharp Sessions Module, **storage providers** are abstractions that define how session data is persisted on the client side. They allow the module to store, retrieve, and remove session information using different storage mechanisms, such as browser local storage, session storage, or custom solutions. By implementing the `StorageProvider` interface, you can customize where and how session data is saved, enabling support for various security and persistence requirements.

Implemented storage providers:
> Please take security requirements into consideration when choosing a storage provider.

### [`LocalStorageProvider`](src/app/modules/besharp-sessions/lib/services/storage-providers/local-storage.provider.ts)

A concrete implementation of the [`StorageProvider`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts) that uses the browser's local storage to persist session information.



## Multiple Sessions

The `beSharp Sessions Module` supports managing multiple sessions simultaneously. This allows users to be logged in with different accounts or different authentication providers at the same time. The [`SessionsService`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts) stores all active sessions and provides methods to switch between them. The [`SessionSelectorComponent`](src/app/modules/besharp-sessions/lib/components/session-selector/session-selector.component.ts) and [`DropdownSessionSelectorComponent`](src/app/modules/besharp-sessions/lib/components/active-session-badge/active-session-badge.component.ts) components provide UI elements for selecting and managing active sessions.

## Multiple Authentication Providers

The `beSharp Sessions Module` supports multiple authentication providers. This allows users to sign in using different methods, such as OAuth2, username/password, or other custom providers. The [`SessionsServiceConfigProvider`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts) is used to configure the available authentication providers. Each provider must implement the [`AuthProvider`](src/app/modules/besharp-sessions/lib/services/sessions.service.ts) interface, which defines the methods for starting and refreshing sessions.

## Automatic Session Refresh and Sign-In

The beSharp Sessions Module can automatically refresh sessions if refresh credentials (such as a refresh token) are made available by the IdP/auth server and supported by the authentication provider class (such as in OAuth2). This ensures that user sessions remain active without requiring manual re-authentication, as long as the authentication provider supports token refresh.

Additionally, when signing in, users may be presented with a "Remember me" or similar checkbox. If this option is selected, the module securely saves the credentials (where supported), allowing for automatic sign-in on subsequent visits without prompting the user for credentials again. This enhances user experience by streamlining the authentication process.

## HTTP Interceptor Integration

The beSharp Sessions Module provides an HTTP interceptor that seamlessly integrates session management with Angular's `HttpClient`. This interceptor automatically attaches authentication tokens from the active session to outgoing HTTP requests, ensuring that API calls are properly authenticated.

### How It Works

- **Token Injection:**  
    When you make an HTTP request using Angular's `HttpClient`, the interceptor checks for an active session via the `SessionsService`. If a valid session is found, it retrieves the access token and adds it to the request's `Authorization` header.

- **Session Validation:**  
    If there is **no active session** or the **active session is expired**, the interceptor will:
    - Trigger the configured `onSignInRequired` callback if no session is available, prompting the user to sign in.
    - Trigger the `onSelectionRequired` callback if multiple sessions exist but none is active, prompting the user to select a session.

- **Automatic Handling:**  
    This process is transparent to the developer. Any HTTP request made with `HttpClient` will automatically use the current session, or initiate the appropriate flow if authentication is required.

### Example

```typescript
// Example HTTP request using Angular HttpClient
this.http.get('/api/protected-resource').subscribe(result => {
    // If a session is active, the request includes the Authorization header.
    // If not, the user is redirected to sign in or select a session.
});
```

## Usage Examples

### Basic Configuration

To use the `beSharp Sessions Module`, you need to import it into your application module:

```typescript
// filepath: src/app/app.module.ts
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { SessionsModule } from './modules/besharp-sessions/besharp-sessions.module';

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    SessionsModule
  ],
  providers: [],
  bootstrap: [AppComponent]
})
export class AppModule { }
```
